import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { userApi } from '../../services/userApi';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { SCircle, SBlock } from '../skeletons';

interface SearchBarProps {
  searchQuery: string;
  handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
}

interface User {
  id: string;
  username: string;
  profilePicture?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  handleSearch,
  isDark,
  goldColor,
  deepGreen
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate(); // Initialize navigate
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userApi.getAllUsers(8);
      
      if (response.success) {
        const transformedUsers = response.users.map((user: any) => ({
          id: user._id,
          username: user.username,
          profilePicture: user.profilePicture
        }));
        
        setUsers(transformedUsers);
        setTotalUsers(response.total || transformedUsers.length);
      } else {
        setError('Failed to fetch users');
      }
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle user profile click
  const handleUserProfileClick = (username: string) => {
    if (username) {
      navigate(`/${username}`);
    }
  };

  const displayedUsers = users.slice(0, 10);
  const remainingUsers = Math.max(0, users.length - 10);

  return (
    <Box sx={{ 
      mb: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 3,
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Search Input */}
      <Box sx={{ 
        width: { xs: '100%', md: '600px', lg: '800px' },
        flexShrink: 0
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search communities..."
          value={searchQuery}
          onChange={handleSearch}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '50px',
              background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)',
              '& fieldset': {
                borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)',
                borderRadius: '50px'
              },
              '&:hover fieldset': { 
                borderColor: goldColor,
                borderWidth: '2px'
              },
              '&.Mui-focused fieldset': { 
                borderColor: goldColor,
                borderWidth: '2px'
              }
            },
            '& .MuiInputBase-input': { 
              color: isDark ? '#FFFFFF' : deepGreen,
              padding: { xs: '12px 16px', md: '12px 16px' },
              fontSize: { xs: '1rem', md: '1.4rem' },
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ 
                  color: goldColor, 
                  opacity: 0.7,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Users section - Hidden on mobile */}
      {!isMobile && (
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end',
          minWidth: 'auto'
        }}>
          {loading ? (
            // Mirrors the avatar stack + caption this is replaced by
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, padding: '2px 20px' }}>
              <Box sx={{ display: 'flex' }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <SCircle key={i} size={32} sx={{ ml: i === 0 ? 0 : '-10px' }} />
                ))}
              </Box>
              <SBlock h={10} w={110} />
            </Box>
          ) : error ? (
            <Typography variant="body2" sx={{ color: goldColor }}>
              {error}
            </Typography>
          ) : (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              padding: '2px 20px',
            }}>
              {/* Stacked avatars */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                height: '48px',
                width: '140px',
                justifyContent: 'flex-end'
              }}>
                {displayedUsers.map((user, index) => (
                  <Tooltip 
                    key={user.id}
                    title={`View ${user.username}'s profile`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: `${index * 28}px`,
                        zIndex: displayedUsers.length - index,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          zIndex: displayedUsers.length + 1, // Bring to front on hover
                          '& .user-avatar': {
                            boxShadow: `0 5px 15px ${isDark ? 'rgba(196, 182, 111, 0.4)' : 'rgba(5, 24, 20, 0.3)'}`,
                            borderColor: goldColor,
                          }
                        }
                      }}
                      onClick={() => handleUserProfileClick(user.username)}
                    >
                      <Avatar
                        src={user.profilePicture}
                        className="user-avatar"
                        sx={{
                          width: 48,
                          height: 48,
                          border: `3px solid ${isDark ? '#000' : '#fff'}`,
                          boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
                          bgcolor: goldColor,
                          color: deepGreen,
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </Avatar>
                    </Box>
                  </Tooltip>
                ))}
                
                {/* + remaining users indicator */}
                {remainingUsers > 0 && (
                  <Tooltip 
                    title={`${remainingUsers} more users`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: `${displayedUsers.length * 28}px`,
                        zIndex: 0,
                        cursor: 'pointer',
                        '&:hover': {
                          '& .remaining-avatar': {
                            bgcolor: goldColor,
                            color: deepGreen,
                            borderColor: goldColor,
                          }
                        }
                      }}
                      onClick={() => {
                        // Optional: You could navigate to a users directory page here
                        // navigate('/users');
                      }}
                    >
                      <Avatar
                        className="remaining-avatar"
                        sx={{
                          width: 48,
                          height: 48,
                          border: `3px solid ${isDark ? '#000' : '#fff'}`,
                          bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                          color: goldColor,
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        +{remainingUsers}
                      </Avatar>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 1.5,
                  color: isDark ? 'rgba(255,255,255,0.7)' : deepGreen,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {totalUsers} TRUSTED USERS
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchBar;